import React from "react"
import { CalendarDays, Clock, Users, Armchair } from "lucide-mui"
import i18n from "@/i18n"

// Icon color #2596fa matches PRIMARY_BLUE in @/constants/colors
const STYLES = {
  sectionNoPadTop: "bg-white px-5 pb-5",
  divider: "border-0 border-t border-solid border-[#f4f4f4] m-0",
  card: "bg-white border border-[#f4f4f4] rounded-[10px] p-4 shadow-[2px_2px_10px_0px_rgba(0,0,0,0.05)]",
  venueName: "text-[18px] font-medium tracking-[-0.5px] text-[#171717]",
  detailRow: "flex items-center justify-between py-[6px]",
  detailLabel: "flex items-center gap-[6px] text-[#929baa] text-[12px]",
  detailValue: "text-[#171717] text-[12px] tracking-[-0.5px]",
  icon: "w-3 h-3 text-[#2596fa]",
  tzAbbr: "text-[#cacaca] text-[10px]",
  refNumber: "text-right text-[#9ca3af] text-[12px] mt-2",
  noteLabel: "text-[#929baa] text-[12px] mb-1",
  noteContent: "bg-[#fcfcfd] rounded-[5px] p-[10px] text-[#171717] text-[12px]",
} as const

type Props = {
  venueName: string
  formattedDate: string
  formattedTimeRange: string
  tzAbbr: string
  peopleAmount: number | null | undefined
  tableName: string
  referenceNumber: string
  customerNote: string | null | undefined
}

const ReservationDetailsCard: React.FC<Props> = ({
  venueName,
  formattedDate,
  formattedTimeRange,
  tzAbbr,
  peopleAmount,
  tableName,
  referenceNumber,
  customerNote,
}) => (
  <>
    <div className={STYLES.sectionNoPadTop}>
      <div className={STYLES.card}>
        <div className={STYLES.venueName}>{venueName}</div>
        <hr className={STYLES.divider + " my-3"} />

        <div className={STYLES.detailRow}>
          <span className={STYLES.detailLabel}>
            <CalendarDays className={STYLES.icon} />
            {i18n.t("app.public.reservation.date")}
          </span>
          <span className={STYLES.detailValue}>{formattedDate}</span>
        </div>

        <div className={STYLES.detailRow}>
          <span className={STYLES.detailLabel}>
            <Users className={STYLES.icon} />
            {i18n.t("app.public.reservation.party_size")}
          </span>
          <span className={STYLES.detailValue}>
            {i18n.t("app.public.reservation.guests", { count: peopleAmount || 0 })}
          </span>
        </div>

        <div className={STYLES.detailRow}>
          <span className={STYLES.detailLabel}>
            <Clock className={STYLES.icon} />
            {i18n.t("app.public.reservation.time")}
          </span>
          <span className={STYLES.detailValue}>
            {formattedTimeRange} <span className={STYLES.tzAbbr}>{tzAbbr}</span>
          </span>
        </div>

        <div className={STYLES.detailRow}>
          <span className={STYLES.detailLabel}>
            <Armchair className={STYLES.icon} />
            {i18n.t("app.public.reservation.table")}
          </span>
          <span className={STYLES.detailValue}>{tableName}</span>
        </div>
      </div>

      {referenceNumber && <div className={STYLES.refNumber}># {referenceNumber}</div>}
    </div>

    {customerNote && (
      <div className={STYLES.sectionNoPadTop}>
        <div className={STYLES.noteLabel}>{i18n.t("app.public.reservation.special_request")}</div>
        <div className={STYLES.noteContent}>{customerNote}</div>
      </div>
    )}
  </>
)

export default ReservationDetailsCard
